// Angular
import { Inject, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Local library
import { CommonFacade, ENVIRONMENT } from '@amwager/data-access';
import { LoadService, MatNotificationService } from '@amwager/util';
import { LoggerService } from '@amwager/util-ts';

// Local file
import { FundingFacade } from '../+state';
import { FundingService } from './funding.service';

/** Business logic for the deposit crypto-currency (BitPay) component. */
@Injectable()
export class DepositCryptoCurrencyService {

  private readonly BIT_PAY_URL = 'https://bitpay.com/bitpay.js';

  constructor(
    private readonly loadService: LoadService,
    private readonly formBuilder: FormBuilder,
    private readonly fundingFacade: FundingFacade,
    private readonly matNotificationService: MatNotificationService,
    private readonly commonFacade: CommonFacade,
    private readonly loggerService: LoggerService,
    private readonly fundingService: FundingService,
    @Inject(ENVIRONMENT) private readonly environment: string,
  ) {}

  /** Build the crypto-currency form. */
  buildForm(): FormGroup {
    return this.formBuilder.group({
      amount: ['', [Validators.required, Validators.minLength(25), Validators.maxLength(10000)]]
    });
  }

  /** Dispatch the crypto-currency deposit and stream the result. */
  deposit$(amount: number) {
    this.fundingFacade.depositCryptoCurrency(amount);
    return this.fundingFacade.isDepositCryptoCurrencySuccess$();
  }

  /** Load the BitPay CDN script (removes any stale copy first). */
  loadBitPayScript(): Promise<void> {
    this.removeBitPayScript();

    const scriptsToAdd = [
      { id: 'bitPay', url: this.BIT_PAY_URL },
    ];

    return this.loadService.addScripts(scriptsToAdd)
      .then(() => { this.loggerService.log('Bitpay scripts added successfully!'); })
      .catch(error => { this.loggerService.log('ERROR: Bitpay scripts loading failed:', error); });
  }

  /** Remove the BitPay CDN script. */
  removeBitPayScript(): Promise<void> {
    const scriptsToRemove = ['bitPay'];

    return this.loadService.removeScripts(scriptsToRemove)
      .then(() => { this.loggerService.log('Bitpay scripts removed successfully!'); })
      .catch(error => { this.loggerService.log('ERROR: Bitpay scripts removal failed:', error); });
  }

  /** Open the BitPay invoice window. */
  openInvoice(invoiceId: string): void {
    this.fundingService.openCryptoInvoice(invoiceId);
  }

  /** Flag the app as offline for the given source. */
  setOffline(source: string): void {
    this.commonFacade.setIsConnectedInternet(source, false);
  }

  /** Mark all controls touched to reveal validation messages. */
  markFormGroupTouched(formGroup: FormGroup): void {
    this.fundingService.markFormGroupTouched(formGroup);
  }

  /** Show a single-button alert. */
  showAlert(header: string, message: string): void {
    this.matNotificationService.showAlert(header, message, undefined, undefined, 'Close');
  }

  /** Watch connectivity and report changes back to the caller. */
  checkConnectivity(source: string, onChange: (online: boolean) => void): void {
    this.fundingService.checkConnectivity(this.environment, source, onChange);
  }

}
